import React, {
    useEffect,
    useState,
    useRef,
    useCallback
} from "react";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import FormTextarea from "@/components/Base/Form/FormTextarea";
import clsx from "clsx";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@/components/Base/Alert";
import Dropzone from "@/components/Base/Dropzone";
import Litepicker from "@/components/Base/Litepicker";

// Helper function to format ISO date to YYYY-MM-DD for input[type="date"]
function formatDateForInput(isoDate: string | null | undefined): string {
    if (!isoDate) return "";

    try {
        // Parse the ISO date and extract just the date part
        const date = new Date(isoDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error("Error formatting date:", error);
        return "";
    }
}

// Fix date parsing to avoid timezone shift
function parseLocalDateOnly(input: string): string {
    if (!input || typeof input !== "string") {
        throw new Error("Invalid date input: " + input);
    }

    const date = new Date(input);
    if (isNaN(date.getTime())) {
        throw new Error("Parsed invalid date from: " + input);
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-based
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
}

type Mode = "add" | "edit";

interface Props {
    /** Optional override. If not provided, will auto-detect from route param `id` */
    mode?: Mode;
}

interface Project {
    id: string;
    name: string;
}

interface TeamCategory {
    id: string;
    teamCategory: string;
    companyId: string;
}

interface Region {
    id: string;
    name: string;
    companyId: string;
}

interface TeamMember {
    id: string;
    userId: string;
    teamId: string;
    user: {
        id: string;
        firstName: string;
        lastName: string;
        username: string;
        email: string;
    };
}

interface FormData {
    name: string;
    description: string;
    regionId: string;
    projectId: string;
    managerId: string;
    teamCatId: string;
    salesTarget: string;
    qrPaymentUrl: string;
    status: string;
    startDate: string;
    endDate: string;
    enableTeamRoute: string;
    companyId: string;
}

interface FormErrors {
    name?: string;
    regionId?:string;
    teamCatId?: string;
}

const EMPTY_FORM: FormData = {
    name: "",
    description: "",
    regionId: "",
    projectId: "",
    managerId: "",
    teamCatId: "",
    salesTarget: "",
    qrPaymentUrl: "",
    status: "true", // Default set to Active
    startDate: "",
    endDate: "",
    enableTeamRoute: "false", // Default set to 'not required'
    companyId: "",
}

export default function EditTeam({ mode: modeProp }: Props) {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [errors, setErrors] = useState<FormErrors>({});
    const [projects, setProjects] = useState<Project[]>([]);
    const [teamCategories, setTeamCategories] = useState<TeamCategory[]>([]);
    const [regions, setRegions] = useState<Region[]>([]);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [imageUrl, setImageUrl] = useState<string>("");
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [imageError, setImageError] = useState("");
    const [showNoMembersAlert, setShowNoMembersAlert] = useState(false);
    const dropzoneRef = useRef<any>();

    // Auto detect mode from URL param
    const mode = modeProp || (id ? "edit" : "add");

    useEffect(() => {
        const init = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            try {
                setLoading(true);

                // Get companyId and projectId from URL params
                const urlParams = new URLSearchParams(window.location.search);
                const companyId = urlParams.get("companyId");
                const projectId = urlParams.get("projectId");

                // Load project
                try {
                    const projectsUrl = companyId ?
                        `${apiUrl}/projects?companyId=${companyId}` :
                        `${apiUrl}/projects`;
                    const projectsRes = await axios.get(projectsUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setProjects(projectsRes.data?.data || []); 
                } catch (err) {
                    console.log("Could not load teams");
                }

                // Load teams
                try {
                    const teamCategoryUrl = companyId ?
                        `${apiUrl}/team-categories?companyId=${companyId}` :
                        `${apiUrl}/team-categories`;
                    const catRes = await axios.get(teamCategoryUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setTeamCategories(catRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load team categories");
                }

                // Load regions
                try {
                    const regionsUrl = companyId ?
                        `${apiUrl}/regions?companyId=${companyId}` :
                        `${apiUrl}/regions`;
                    const groupsRes = await axios.get(regionsUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setRegions(groupsRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load regions");
                }

                // If editing, load the team
                if (mode === "edit" && id) {
                    const teamRes = await axios.get(`${apiUrl}/teams/${id}`, {
                        headers: {Authorization: `Bearer ${token}` }
                    });
                    const team = teamRes.data;

                    setFormData({
                        name: team.name || "",
                        description: team.description || "",
                        regionId: team.regionId || "",
                        projectId: team.projectId || "",
                        managerId: team.managerId || "",
                        teamCatId: team.teamCatId || "",
                        salesTarget: team.salesTarget?.toString() || "",
                        qrPaymentUrl: team.qrPaymentUrl || "",
                        status: team.status ? "true" : "false",
                        startDate: team.startDate?.slice(0, 10) || "",
                        endDate: team.endDate?.slice(0, 10) || "",
                        enableTeamRoute: team.enableTeamRoute ? "true" : "false",
                        companyId: team.companyId || "",
                    });
                    setImageUrl(team.qrPaymentUrl || "");

                    // Load team members
                    try {
                        const membersRes = await axios.get(`${apiUrl}/teams/${id}/members`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        setTeamMembers(membersRes.data?.data || []);
                    } catch (err) {
                        console.log("Could not load team members");
                        setTeamMembers([]);
                    }

                } else if (companyId || projectId) {
                    // Pre-fill companyId and projectId for new teams
                    setFormData(prev => ({ 
                        ...prev, 
                        companyId: companyId || prev.companyId,
                        projectId: projectId || prev.projectId
                    }));
                }

            } catch (error) {
                console.error("Failed to initialize:", error);
                setSubmitError("Failed to load data");
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [mode, id]);

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleManagerSelectClick = () => {
        if (mode === "edit" && teamMembers.length === 0) {
            setShowNoMembersAlert(true);
            setTimeout(() => setShowNoMembersAlert(false), 5000);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Team name is required";
        }

        if (!formData.regionId.trim()) {
            newErrors.regionId = "Please select region";
        }

        if (!formData.teamCatId.trim()) {
            newErrors.teamCatId = "Please select team category";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length ===0;
    };

    // Handle image upload
    const handleImageUpload = useCallback(async (files: File[] | File) => {
        const fileArray = Array.isArray(files) ? files: [files];

        if (fileArray.length === 0) return;

        const file = fileArray[0];

        if (!file.type.startsWith('image/')) {
            setImageError('Please select an image');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setImageError('Image size must be less than 5MB');
        }

        setIsUploadingImage(true);
        setImageError("");

        try {
            const formData = new FormData();
            formData.append('image', file);

            const apiUrl = import.meta.env.API_VITE_URL || 'http://localhost:4000';
            const token = getToken();

            const response = await axios.post(`${apiUrl}/upload/image`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const uploadedImageUrl = response.data.data.url;
                setImageUrl(uploadedImageUrl);
                setFormData(prev => ({ ...prev, qrPaymentUrl: uploadedImageUrl}));
            }
        } catch (error: any) {
            setImageError(error?.response?.data?.message || 'Failed to upload image');
        } finally {
            setIsUploadingImage(false);
        }
    }, []);

    // Handle image removal
    const handleImageRemove = async () => {
        if (!imageUrl) return;
        
        try {
            // Extract the S3 key from the URL
            const url = new URL(imageUrl);
            const s3Key = url.pathname.substring(1) // Remove leading slash

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Call backend to delete from S3
            await axios.delete(`${apiUrl}/upload/image`, {
                headers: { Authorization: `Bearer ${token}`},
                data: {key: s3Key}
            });

            // Clear local state
            setImageUrl("");
            setFormData(prev => ({ ...prev, qrPaymentUrl: "" }));
            setImageError("");

            console.log('Image delete from S3 successfully');
        } catch (error: any) {
            console.error('Failed to delete image from S3', error);
            setImageError('Failed to delete image from S3');

            // Still clear the UI if S3 deletion fails
            setImageUrl("");
            setFormData(prev => ({ ...prev, qrPaymentUrl: "" }));
        }
    };

    // Handle drag and drop
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleImageUpload(files)
        }
    }, [handleImageUpload]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []); 

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setSubmitError("");
        setSubmitSuccess("");

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Prepare data for API
            const apiData = {
                name: formData.name,
                description: formData.description || undefined,
                regionId: formData.regionId || undefined,
                projectId: formData.projectId || undefined,
                managerId: formData.managerId || undefined,
                teamCatId: formData.teamCatId || undefined,
                salesTarget: formData.salesTarget ? Number(formData.salesTarget) : undefined,
                qrPaymentUrl: formData.qrPaymentUrl || undefined,
                status: formData.status === "true",
                startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
                endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
                enableTeamRoute: formData.enableTeamRoute === "false",
                companyId: formData.companyId || undefined,
            };

            if (mode ==="add") {
                const queryParams = new URLSearchParams();
                if (formData.companyId) {
                    queryParams.append("companyId", formData.companyId);
                }

                await axios.post(
                    `${apiUrl}/teams?${queryParams.toString()}`,
                    apiData,
                    { headers: { Authorization: `Bearer ${token}` }}
                );
                setSubmitSuccess("Team created successfully!");
            } else {
                await axios.put(
                    `${apiUrl}/teams/${id}`,
                    apiData,
                    { headers: { Authorization: `Bearer ${token}` }}
                );
                setSubmitSuccess("Team updated successfully!");
            }

            // Redirect after success
            setTimeout(() => {
                const searchParams = new URLSearchParams(window.location.search);
                const projectId = searchParams.get("projectId");
                const companyId = formData.companyId;

                let redirectUrl = "/teams";
                if (projectId) {
                    redirectUrl += `?projectId=${projectId}`;
                } else if (companyId) {
                    redirectUrl += `?companyId=${companyId}`;
                }

                navigate(redirectUrl);
            }, 1500);
        } catch (error: any) {
            console.error("Submit failed:", error);
            const message = error.response?.data?.message || error.message || "An error occurred";
            setSubmitError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;

        let redirectUrl = "/teams";
        if (projectId) {
            redirectUrl += `?projectId=${projectId}`;
        } else if (companyId) {
            redirectUrl += `?companyId=${companyId}`;
        }

        navigate(redirectUrl);
    };

    if (loading && mode === "edit") {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading team...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 max-w-7xl mx-auto">
            <div className="col-span-12">
                <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
                    <div className="text-base font-medium group-[.mode--light]:text-white">
                        {mode === "add" ? "Add Team" : "Edit Team"}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                        <Button
                            variant="outline-secondary"
                            className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                            onClick={() => {
                                const searchParams = new URLSearchParams(window.location.search);
                                const projectId = searchParams.get("projectId");
                                const companyId = formData.companyId;

                                let assignUserUrl = `/teams/${id}/assign-user`;
                                if (projectId) {
                                    assignUserUrl += `?projectId=${projectId}`;
                                } else if (companyId) {
                                    assignUserUrl += `?companyId=${companyId}`;
                                }

                                navigate(assignUserUrl);
                            }}
                        >
                            <Lucide icon="UserPlus" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                            Assign User
                        </Button>

                        <Button
                            variant="outline-secondary"
                            className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                            onClick={() => {
                                const searchParams = new URLSearchParams(window.location.search);
                                const projectId = searchParams.get("projectId");
                                const companyId = formData.companyId;

                                let assignOutletUrl = `/teams/${id}/assign-outlet`;
                                if (projectId) {
                                    assignOutletUrl += `?projectId=${projectId}`;
                                } else if (companyId) {
                                    assignOutletUrl += `?companyId=${companyId}`;
                                }

                                navigate(assignOutletUrl);
                            }}
                        >
                            <Lucide icon="Store" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                            Assign Outlet
                        </Button>

                        <Button
                            variant="outline-secondary"
                            className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                            onClick={() => {
                                const searchParams = new URLSearchParams(window.location.search);
                                const projectId = searchParams.get("projectId");
                                const companyId = formData.companyId;

                                let assignProductUrl = `/teams/${id}/assign-product`;
                                if (projectId) {
                                    assignProductUrl += `?projectId=${projectId}`;
                                } else if (companyId) {
                                    assignProductUrl += `?companyId=${companyId}`;
                                }

                                navigate(assignProductUrl);
                            }}
                        >
                            <Lucide icon="Package" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                            Assign Product
                        </Button>

                    </div>
                    
                </div>

                

                <div className="mt-3.5">
                    <div className="flex flex-col box box--stacked max-w-full overflow-hidden">
                        <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                            <div className="flex-1">
                                <form onSubmit={handleSubmit}>
                                    <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                                        <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                                            <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                                            Team Information
                                        </div>

                                        {submitError && (
                                            <Alert variant="outline-danger" className="mt-5">
                                                <Lucide icon="AlertTriangle" className="w-6 h-6" />
                                                {submitError}
                                            </Alert>
                                        )}

                                        {submitSuccess && (
                                            <Alert variant="outline-success" className="mt-5">
                                                <Lucide icon="CheckCircle" className="w-6 h-6" />
                                                {submitSuccess}
                                            </Alert>
                                        )}

                                        <div className="mt-5 grid grid-cols-12 gap-4">
                                        

                                        {/* Team Name */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Team Name <span className="text-danger">*</span>
                                            </label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter team name"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange("name", e.target.value)}
                                                className={clsx({ "border-danger": errors.name })}
                                            />
                                            {errors.name && (
                                                <div className="mt-2 text-danger text-xs">{errors.name}</div>
                                            )}
                                        </div>

                                        {/* Description */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Description</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter team description"
                                                value={formData.description}
                                                onChange={(e) => handleInputChange("description", e.target.value)}
                                            />
                                        </div>

                                        {/* Region */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Region <span className="text-danger">*</span>
                                            </label>
                                            <FormSelect
                                                value={formData.regionId}
                                                onChange={(e) => handleInputChange("regionId", e.target.value)}
                                                className={clsx({ "border-danger": errors.regionId })}
                                            >
                                                <option value="">Select a region</option>
                                                {regions.map((group) => (
                                                    <option key={group.id} value={group.id}>
                                                        {group.name}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            {errors.regionId && (
                                                <div className="mt-2 text-danger text-xs">{errors.regionId}</div>
                                            )}
                                        </div>

                                        {/* Team Category */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Team Category <span className="text-danger">*</span>
                                            </label>
                                            <FormSelect
                                                value={formData.teamCatId}
                                                onChange={(e) => handleInputChange("teamCatId", e.target.value)}
                                                className={clsx({ "border-danger": errors.teamCatId })}
                                            >
                                                <option value="">Select a category</option>
                                                {teamCategories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.teamCategory}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            {errors.teamCatId && (
                                                <div className="mt-2 text-danger text-xs">{errors.teamCatId}</div>
                                            )}
                                        </div>


                                        {/* Start Date*/}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Start Date</label>
                                                <Litepicker
                                                    value={formData.startDate || ""}
                                                    onChange={(e) => {
                                                        const selected = e.target.value;
                                                        try {
                                                            if (selected) {
                                                                const fixedDate = parseLocalDateOnly(selected);
                                                                setFormData((prev) => ({ ...prev, startDate: fixedDate }));
                                                            }
                                                        } catch (err) {
                                                            console.error("Start date parse error:", err);
                                                        }
                                                    }}
                                                    options={{
                                                        autoApply: true,
                                                        dropdowns: {
                                                            minYear: 1990,
                                                            maxYear: null,
                                                            months: true,
                                                            years: true,
                                                        },
                                                    }}
                                                />
                                        </div>

                                        {/* End Date*/}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">End Date</label>
                                                <Litepicker
                                                    value={formData.endDate || ""}
                                                    onChange={(e) => {
                                                        const selected = e.target.value;
                                                        try {
                                                            if (selected) {
                                                                const fixedDate = parseLocalDateOnly(selected);
                                                                setFormData((prev) => ({ ...prev, endDate: fixedDate }));
                                                            }
                                                        } catch (err) {
                                                            console.error("End date parse error:", err);
                                                        }
                                                    }}
                                                    options={{
                                                        minDate: formData.startDate || undefined,
                                                        autoApply: true,
                                                        dropdowns: {
                                                            minYear: 1990,
                                                            maxYear: null,
                                                            months: true,
                                                            years: true,
                                                        },
                                                    }}
                                                />
                                        </div>

                                        {/* Sales Target */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Sales Target</label>
                                            <FormInput
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                value={formData.salesTarget}
                                                onChange={(e) => handleInputChange("salesTarget", e.target.value)}
                                            />
                                        </div>

                                        {/* Set Supervisor/Manager */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Supervisor/Team Leader</label>
                                            {mode === "add" ? (
                                                <div className="p-3 bg-slate-100 dark:bg-darkmode-800 rounded-md text-slate-600 dark:text-slate-400 text-sm">
                                                    Please save the team first, then assign members before selecting a supervisor.
                                                </div>
                                            ) : (
                                                <>
                                                    <FormSelect
                                                        value={formData.managerId}
                                                        onChange={(e) => handleInputChange("managerId", e.target.value)}
                                                        onClick={handleManagerSelectClick}
                                                        disabled={teamMembers.length === 0}
                                                    >
                                                        <option value="">Select a team leader</option>
                                                        {teamMembers.map((member) => (
                                                            <option key={member.userId} value={member.userId}>
                                                                {member.user.firstName} {member.user.lastName} ({member.user.username})
                                                            </option>
                                                        ))}
                                                    </FormSelect>
                                                    {showNoMembersAlert && teamMembers.length === 0 && (
                                                        <Alert variant="outline-warning" className="mt-2">
                                                            <Lucide icon="AlertCircle" className="w-4 h-4" />
                                                            <div className="ml-2">
                                                                No members assigned to this team yet. Please{" "}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const searchParams = new URLSearchParams(window.location.search);
                                                                        const projectId = searchParams.get("projectId");
                                                                        const companyId = formData.companyId;
                                                                        let assignUserUrl = `/teams/${id}/assign-user`;
                                                                        if (projectId) {
                                                                            assignUserUrl += `?projectId=${projectId}`;
                                                                        } else if (companyId) {
                                                                            assignUserUrl += `?companyId=${companyId}`;
                                                                        }
                                                                        navigate(assignUserUrl);
                                                                    }}
                                                                    className="font-medium underline text-warning hover:text-warning/80"
                                                                >
                                                                    assign members
                                                                </button>
                                                                {" "}first before selecting a supervisor.
                                                            </div>
                                                        </Alert>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Status */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Status</label>
                                            <FormSelect
                                                value={formData.status}
                                                onChange={(e) => handleInputChange("status", e.target.value)}
                                            >
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </FormSelect>
                                        </div>

                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-between mt-8 gap-3">
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={() => navigate(-1)}
                                        >
                                            <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                                            Back
                                        </Button>
                                        <div className="flex gap-3">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                onClick={handleCancel}
                                                disabled={loading}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={loading}
                                            >
                                                {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                                                {mode === "add" ? "Create Team" : "Update Team"}
                                            </Button>
                                        </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}